import { reactive, computed } from 'vue';
import { classifyDataError } from '@/utils/reportQueryErrors';

// Phase 2 (2026-08-25, silent read failures).
//
// WHY THIS EXISTS RATHER THAN AN `error` REF PER CALL SITE.
//
// getCollection already returned an `error` ref. It made no difference, because
// of 37 call sites across 22 files, exactly 2 destructured it (Locations.vue and
// People.vue) and the other 35 read only `documents`. A denied read set
// `documents.value = []` and `loading.value = false`, so the roster rendered as a
// fully-loaded, genuinely-empty week — visually identical to a quiet Tuesday. The
// entire trace was one console.error.
//
// Adding a better `error` ref would have changed nothing: the 35 consumers that
// ignore the current one would ignore the new one too, and every future call site
// would have to remember. Relying on that memory is exactly how this happened.
//
// So the failure is reported HERE, app-wide, and rendered once by App.vue. A
// consumer cannot accidentally render a failure as success, because the consumer
// is not the thing doing the rendering. Nothing about the success path changes,
// and no call site has to be edited for the fix to hold.
//
// Plain reactive object, not Pinia — matching notificationStore.js, the existing
// convention for a store that is app-wide state rather than persisted user state
// (see CLAUDE.md). Deliberately NOT persisted: a data-health problem is a fact
// about this session's connection, and reviving yesterday's terminal banner from
// localStorage on a fresh, healthy boot would be its own false alarm.

const state = reactive({
  // The first TERMINAL failure seen this session, or null.
  // { code, title, message, action, source }
  // First, not latest: once access is revoked every listener on the page fails
  // in a burst, and the first one is the honest cause. Letting later ones
  // overwrite it makes the message flicker between listeners for no gain.
  terminal: null,
  // Sources currently in a TRANSIENT failure state, e.g.
  // 'organisations/org1/shifts'. A Set-like array so a source that recovers can
  // remove exactly its own entry without clearing everyone else's.
  transientSources: [],
});

const hasTransient = computed(() => state.transientSources.length > 0);

/**
 * Record that a read failed.
 *
 * @param {string} source  the collection/document path — identifies the reader
 *                         so its own recovery clears its own entry
 * @param {{code?: string}} err  a FirestoreError
 * @returns {{terminal: boolean}} so the caller can decide whether to clear its data
 */
const reportFailure = (source, err) => {
  const classified = classifyDataError(err);

  if (classified.terminal) {
    // console.error here, not at the call site, so every reporter logs the same
    // shape and a denial is never silent again regardless of who reported it.
    console.error(`[data-health] terminal read failure on ${source}:`, err?.code || err);
    if (!state.terminal) {
      state.terminal = { ...classified, source };
    }
    return { terminal: true };
  }

  console.error(`[data-health] transient read failure on ${source}:`, err?.code || err);
  if (!state.transientSources.includes(source)) {
    state.transientSources.push(source);
  }
  return { terminal: false };
};

/**
 * Record that a read SUCCEEDED, clearing any transient failure for that source.
 *
 * Deliberately does not clear `terminal`. A terminal failure means access is
 * gone; a later success from some other listener that happens to still be
 * permitted must not wipe the message telling the person why the page is empty.
 * Terminal state is cleared only by resetDataHealth() — i.e. by signing out.
 */
const reportSuccess = (source) => {
  const idx = state.transientSources.indexOf(source);
  if (idx !== -1) state.transientSources.splice(idx, 1);
};

/** Full reset. For logout, and for test isolation. */
const resetDataHealth = () => {
  state.terminal = null;
  state.transientSources.splice(0);
};

export default {
  state,
  hasTransient,
  reportFailure,
  reportSuccess,
  resetDataHealth,
};
